import React, { useState, useEffect } from 'react';
import api from '../api';
import { FaSolarPanel, FaMapMarkerAlt, FaAward, FaBolt, FaUsers, FaLeaf } from 'react-icons/fa';

const Statistics = () => {
  const [stats, setStats] = useState([]);
  const [isVisible, setIsVisible] = useState(false);

  // --- ICON MAP ---
  const iconMap = {
    "FaSolarPanel": <FaSolarPanel />,
    "FaMapMarkerAlt": <FaMapMarkerAlt />,
    "FaAward": <FaAward />,
    "FaBolt": <FaBolt />,
    "FaUsers": <FaUsers />,
    "FaLeaf": <FaLeaf />
  };



  // --- 1. Fetch Data ---
  useEffect(() => {
    const fetchStats = async () => {
      try {
       const res = await api.get('/Statistics');
        if (res.data && res.data.length > 0) {
          setStats(res.data);
        } else {
          // Fallback Data
          setStats([
            { value: '500', suffix: '+', label: 'Solar Systems', iconName: 'FaSolarPanel', colorHex: '#00D084', description: 'Across Bihar & Jharkhand' },
            { value: '5', suffix: 'MW+', label: 'Capacity', iconName: 'FaBolt', colorHex: '#FF9800', description: 'Clean energy generated' }
          ]);
        }
      } catch (err) {
        console.error("Error fetching stats:", err);
      }
    };
    fetchStats();
  }, []);

  // --- 2. Intersection Observer (Scroll Animation) ---
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const element = document.getElementById('statistics-section');
    if (element) observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, []);

  return (
    <section 
      id="statistics-section"
      className="statistics-section py-5"
      style={{
        background: 'linear-gradient(135deg, #1B3F6E 0%, #2E5C8A 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Pattern */}
      <div 
        className="position-absolute w-100 h-100"
        style={{
          top: 0, left: 0,
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M30 30l30-30v30H30z\' fill=\'%2300D084\' fill-opacity=\'0.05\'/%3E%3C/svg%3E")',
          opacity: 0.3
        }}
      ></div>

      <div className="container position-relative" style={{ zIndex: 2 }}>
        {/* Section Header */}
        <div className="text-center mb-5">
          <span className="badge bg-success mb-3 px-3 py-2">
            <i className="bi bi-graph-up-arrow me-2"></i>
            Our Impact
          </span>
          <h2 className="display-5 fw-bold text-white mb-3">
            RENGIE by Numbers
          </h2>
          <p className="lead text-white-50">
            Powering Bihar & Jharkhand with clean, reliable solar energy
          </p>
        </div>

        {/* Stats Grid (Dynamic) */}
        <div className="row g-4">
          {stats.map((stat, index) => (
            <div className="col-lg-3 col-md-6" key={index}>
              <div 
                className="stat-card text-center p-4 h-100"
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.boxShadow = `0 15px 40px rgba(0, 208, 132, 0.3)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Icon */}
                <div 
                  className="stat-icon mx-auto mb-3"
                  style={{
                    width: '80px', height: '80px',
                    background: `linear-gradient(135deg, ${stat.colorHex} 0%, ${stat.colorHex}dd 100%)`,
                    borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '2.5rem', color: '#ffffff',
                    boxShadow: `0 8px 25px ${stat.colorHex}40`,
                    animation: isVisible ? 'bounceIn 0.8s ease' : 'none',
                    animationDelay: `${index * 0.1}s`
                  }}
                >
                  {iconMap[stat.iconName] || <FaAward />}
                </div>

                {/* Value with Counter Animation */}
                <h2 
                  className="stat-value fw-bold mb-2"
                  style={{
                    fontSize: '3rem', color: '#00D084',
                    textShadow: '0 0 30px rgba(0, 208, 132, 0.5)',
                    fontFamily: 'system-ui',
                    animation: isVisible ? 'fadeInUp 0.8s ease' : 'none',
                    animationDelay: `${index * 0.1 + 0.2}s`
                  }}
                >
                  {isVisible && (
                    <CountUp end={parseInt(stat.value)} duration={2} delay={index * 0.1} />
                  )}
                  <span style={{ color: '#00D084', fontSize: '0.6em', verticalAlign: 'middle', marginLeft: '5px' }}>
                    {stat.suffix}
                  </span>
                </h2>

                {/* Label */}
                <p className="stat-label fw-semibold mb-2" style={{ fontSize: '1.1rem', color: '#ffffff' }}>
                  {stat.label}
                </p>

                {/* Description */}
                <p className="stat-description small mb-0" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {stat.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer Info (Static) */}
        <div className="row mt-5">
          <div className="col-lg-8 mx-auto">
            <div className="card border-0 text-center p-4" style={{ background: 'rgba(0, 208, 132, 0.15)', backdropFilter: 'blur(10px)', borderRadius: '15px', border: '1px solid rgba(0, 208, 132, 0.3)' }}>
              <p className="text-white mb-3 fw-semibold"><i className="bi bi-patch-check-fill text-success me-2"></i>Empanelled with SBPDCL, NBPDCL & JBVNL | DPIIT Recognized</p>
              <div className="d-flex justify-content-center gap-4 flex-wrap">
                <span className="text-white-50 small"><i className="bi bi-geo-alt-fill text-success me-1"></i>Patna | Ranchi</span>
                <span className="text-white-50 small"><i className="bi bi-telephone-fill text-success me-1"></i>+91-9031654981</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounceIn {
          0% { opacity: 0; transform: scale(0.3) translateY(30px); }
          50% { transform: scale(1.05); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

// Counter Animation Component
const CountUp = ({ end, duration, delay }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp + delay * 1000;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      
      // Handle NaN if data is missing
      const endVal = isNaN(end) ? 0 : end;
      setCount(Math.floor(progress * endVal));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [end, duration, delay]);

  return <>{count}</>;
};

export default Statistics;